import React from "react";
import { Box, Button } from "@mui/material";
import { noop } from "lodash";

import { AsyncOperationState } from "fond/async/redux";
import { PolygonSelect } from "fond/project/panels/polygon/usePolygonSelect";
import Upload from "fond/project/panels/upload/Upload";

import TaskProgress from "./TaskProgress";

const POLYGON_LAYER = { id: "polygon", geometryType: "Polygon", name: "Polygon" } as const;

interface PolygonFileUploadProps {
  polygonSelect: PolygonSelect;
  /** Called when the user chooses to upload the selected file. */
  onUpload(): void;
  onCancel(): void;
}

/**
 * Picker for a polygon file, or why the file couldn't be read; the task the file is read for shows its progress.
 */
const PolygonFileUpload: React.FC<PolygonFileUploadProps> = ({ polygonSelect, onUpload, onCancel }) => {
  const { upload, selectedFiles, setSelectedFiles, dismissPolygonUploadError } = polygonSelect;

  if (upload?.status === AsyncOperationState.failure) {
    return (
      <TaskProgress
        error={{
          title: "Couldn't read the area file",
          reason: upload.failureMessage || "Check the file contains a polygon and try again.",
          actions: [
            { label: "Cancel", onClick: onCancel },
            { label: "Choose another file", onClick: dismissPolygonUploadError, primary: true },
          ],
        }}
      />
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Upload
        layer={POLYGON_LAYER}
        onSelectFiles={setSelectedFiles}
        autoUpload={false}
        onBeginUpload={noop}
        onRemove={() => setSelectedFiles(null)}
        includeLayerName={false}
        convertToDualSidedField={null}
      />
      <Box display="flex" justifyContent="flex-end" gap={0.5}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" size="small" onClick={onUpload} disabled={!selectedFiles?.length}>
          Upload
        </Button>
      </Box>
    </Box>
  );
};

export default PolygonFileUpload;
